import moment from "moment";
import {
  s3baseUrl,
  peerHost,
  peerPort,
  peerPath,
  peerSecure,
  peerKey as configPeerKey,
  turnIceUrls,
} from "../config/config";
import logger from "./logger";

export const PORTAL_TYPE = "associate";

export const USER_TYPE = Object.freeze({
  STUDENT: "student",
  ADMIN: "admin",
  ASSOCIATE: "associate",
  TEACHER: "teacher",
  CLIENT: "client",
});

/** Recipients whose read/delivery status drives staff tick icons. */
export const isMessageStatusRecipientType = (userType) =>
  userType === USER_TYPE.STUDENT || userType === USER_TYPE.CLIENT;

export const BASED_ON = Object.freeze({
  STUDENT: "student",
  CLASS: "class",
});

export const isStaffCanHardDeleteMessage = (deleterUserType) =>
  deleterUserType === USER_TYPE.ADMIN || deleterUserType === USER_TYPE.ASSOCIATE;

export const getStudentMemberFromMembers = (members) =>
  members?.find((m) => m.user_type === USER_TYPE.STUDENT) ?? null;

export const CALL_INFO = {
  calling: {
    call_text: "Calling...",
    call_icon: "simple-line-icons:call-out",
  },
  ringing: {
    call_text: "Ringing...",
    call_icon: "tdesign:call-1",
  },
  canceled: {
    call_text: "Canceled",
    call_icon: "tdesign:call-off",
  },
  declined: {
    call_text: "Declined",
    call_icon: "heroicons-outline:phone-missed-call",
  },
  started: {
    call_text: "Started",
    call_icon: "icon-park-outline:phone-video-call",
  },
  no_answered: {
    call_text: "No Answered",
    call_icon: "subway:call-3",
  },
  disconnected: {
    call_text: "Disconnected",
    call_icon: "tdesign:call-off",
  },
  ended: {
    call_text: "Ended",
    call_icon: "ic:baseline-call-end",
  },
  unavailable: {
    call_text: "Unavailable",
    call_icon: "iconamoon:unavailable-fill",
  },
  permissions_error: {
    call_text: "Permissions Error",
    call_icon: "iconamoon:unavailable-fill",
  },
};

const RTL_LANGUAGES = new Set(["ar", "he", "iw", "fa", "ur", "sd", "ku", "yi"]);
export const isRTL = (languageCode) => RTL_LANGUAGES.has(languageCode);

export const formatCallDuration = (startTime, endTime) => {
  const total_time = moment(endTime).diff(moment(startTime), "seconds");
  const hours = Math.floor(total_time / 3600);
  const minutes = Math.floor((total_time % 3600) / 60);
  const seconds = Math.floor((total_time % 3600) % 60);

  return [
    hours > 0 ? `${hours}h` : "",
    minutes > 0 ? `${minutes}m` : "",
    seconds > 0 ? `${seconds}s` : "",
  ]
    .filter(Boolean)
    .join(" ");
};

export const CHAT_MESSAGE_FILTERS = [
  { icon: "majesticons:chat-text-line", titleKey: "chatting.messageFilter.chat", value: "" },
  { icon: "solar:gallery-wide-bold", titleKey: "chatting.messageFilter.gallery", value: "gallery" },
  { icon: "fa6-regular:star", titleKey: "chatting.messageFilter.saved", value: "saved" },
  { icon: "bi:broadcast-pin", titleKey: "chatting.messageFilter.broadcast", value: "broadcast" },
];

export const CHAT_FILTERS = [
  { icon: "majesticons:chat-text-line", titleKey: "chatting.sidebarFilter.chats", value: "all" },
  { icon: "solar:chat-unread-linear", titleKey: "chatting.sidebarFilter.unread", value: "unread" },
  { icon: "fa6-regular:star", titleKey: "chatting.sidebarFilter.favorites", value: "favorites" },
  { icon: "mdi:call-outline", titleKey: "chatting.sidebarFilter.calls", value: "calls" },
  {
    icon: "fluent:contact-card-32-regular",
    titleKey: "chatting.sidebarFilter.contacts",
    value: "contacts",
  },
];

export const DASHBOARD_TABS = [
  { title: "Feeds", value: "feeds" },
  { title: "Stats", value: "stats" },
];

export const sum_unread_messages = (conversation_data, userIdToSum) => {
  let totalUnreadMessages = 0;
  conversation_data.forEach((conversation) => {
    const member = conversation.members.find(
      (member) => member._id === userIdToSum
    );
    if (member) {
      totalUnreadMessages += member.unread_message_count;
    }
  });
  return totalUnreadMessages;
};

export const scroll_to_bottom_on_message = (is_quick_move) => {
  const chat_box = document.getElementById("chat-box-body");
  setTimeout(() => {
    if (chat_box) {
      if (is_quick_move) {
        chat_box.scrollTop = chat_box.scrollHeight;
      } else {
        chat_box.scroll({ top: chat_box.scrollHeight, behavior: "smooth" });
      }
    }
  }, 50);
};

export const MAX_RECORDING_MINUTES = 5;

export const is_file_uploaded = (file) =>
  typeof file === "string" && file.includes("upload");

export const markdown_conversion = (string) => {
  if (!string) return;
  let noAsteriskString = string.replace(/\*/g, "");
  noAsteriskString = noAsteriskString.replace(/\#/g, "");
  const markdownLinkPattern = /\[([^\]]+)\]\([^\)]+\)/g;
  const result = noAsteriskString.replace(
    markdownLinkPattern,
    (match, capturedText) => {
      return capturedText;
    }
  );

  return result;
};

export const markDownLink = (props) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {props.children}
  </a>
);

export const CALL_STATUS = [
  "calling",
  "ringing",
  "canceled",
  "declined",
  "started",
  "disconnected",
  "ended",
  "no_answered",
  "unavailable",
  "permissions_error",
];

export const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export function filter_chats(
  conversationsList,
  userInfo,
  query,
  filterType,
  onlineType
) {
  let dataToFilter = [...conversationsList];

  if (filterType === "unread") {
    dataToFilter = dataToFilter.filter((conversation) => {
      const currentUser = conversation.members.find(
        (user) => user._id === userInfo._id
      );
      return currentUser?.unread_message_count > 0;
    });
  } else if (filterType === "favorites") {
    dataToFilter = dataToFilter.filter(
      (conversation) => conversation.is_favorite === true
    );
  } else if (filterType === "calls") {
    dataToFilter = dataToFilter.filter(
      (conversation) => conversation.last_message?.message_type === "call"
    );
  }

  if (onlineType === "online") {
    dataToFilter = dataToFilter.filter((conversation) =>
      conversation.members.find(
        (member) => member.user_type === USER_TYPE.STUDENT && member.is_online === true
      )
    );
  }

  // Step 2: Apply search query filter
  if (query) {
    const q = query.toLowerCase();
    dataToFilter = dataToFilter.filter((conversation) => {
      if (conversation.based_on === BASED_ON.CLASS) {
        return (conversation.classTitle || "").toLowerCase().includes(q);
      }
      return conversation.members.some(
        (member) =>
          member.user_type === USER_TYPE.STUDENT &&
          (member.name || "").toLowerCase().includes(q)
      );
    });
  }

  const sortedConversations = dataToFilter.sort((a, b) => {
    if (a.is_favorite && !b.is_favorite) return -1;
    if (!a.is_favorite && b.is_favorite) return 1;

    const createdAtA = new Date(a.last_message?.createdAt || a.createdAt);
    const createdAtB = new Date(b.last_message?.createdAt || b.createdAt);
    return createdAtB - createdAtA;
  });

  return sortedConversations;
}

export const date_to_display_for_messages = (date) => {
  const currentDate = moment();
  const givenDate = moment(date);

  if (givenDate.isSame(currentDate, "day")) {
    return moment(date).format("hh:mm A"); // Show time only
  }

  if (givenDate.isAfter(currentDate.clone().subtract(1, "week"))) {
    return moment(date).format("dddd [at] hh:mm A"); // Show day only
  }

  return moment(date).format("YYYY-MM-DD [at] hh:mm A");
};

export const date_to_display = (date) => {
  const currentDate = moment();
  const givenDate = moment(date);

  if (givenDate.isSame(currentDate, "day")) {
    return moment(date).format("hh:mm A"); // Show time only
  }

  if (givenDate.isAfter(currentDate.clone().subtract(1, "week"))) {
    return moment(date).format("dddd"); // Show day only
  }

  return moment(date).format("YYYY-MM-DD");
};

export const get_ended_text = (call) => {
  const { call_status, call_started_time, call_ended_time } = call;
  let ended_text = "00:00:00";
  if (call_status === "ended") {
    const startTime = moment(call_started_time);
    const endTime = moment(call_ended_time);
    const total_time = endTime.diff(startTime, "seconds");
    let hours = Math.floor(total_time / 3600);
    let minutes = Math.floor((total_time % 3600) / 60);
    let seconds = Math.floor((total_time % 3600) % 60);

    if (hours > 0) {
      ended_text = `${hours < 10 ? "0" + hours : hours}:`;
    } else {
      ended_text = `00:`;
    }
    if (minutes > 0) {
      ended_text += `${minutes < 10 ? "0" + minutes : minutes}:`;
    } else {
      ended_text += `00:`;
    }
    if (seconds > 0) {
      ended_text += `${seconds < 10 ? "0" + seconds : seconds}`;
    } else {
      ended_text += `00`;
    }
  }
  return ended_text;
};

export const isEmptyMessage = (html) => {
  if (!html) return true;
  const isEmpty =
    html === "" ||
    html === "\n" ||
    html === "<br>" ||
    html === "<br/>" ||
    html === "<br />" ||
    html === "&nbsp;";
  return isEmpty;
};

export const markupToMarkdown = (editorContent) => {
  if (!editorContent) return;
  const clone = editorContent.cloneNode(true);

  const blockElements = clone.querySelectorAll(
    "p, div, pre, h1, h2, h3, h4, h5, h6"
  );
  blockElements.forEach((node) => {
    const text = node.textContent;
    const replacement = document.createTextNode(`\n${text}\n`);
    node.replaceWith(replacement);
  });

  // Convert <br> tags to explicit newlines
  const breaks = clone.querySelectorAll("br");
  breaks.forEach((node) => {
    node.replaceWith(document.createTextNode("\n"));
  });

  const boldNodes = clone.querySelectorAll("b, strong");
  boldNodes.forEach((node) => {
    node.replaceWith(document.createTextNode(`**${node.textContent}**`));
  });

  const italicNodes = clone.querySelectorAll("i, em");
  italicNodes.forEach((node) => {
    node.replaceWith(document.createTextNode(`*${node.textContent}*`));
  });

  clone.querySelectorAll("br").forEach((br) => {
    br.replaceWith(document.createTextNode("\n"));
  });

  let text = clone.innerText.trim();
  /* Pasted URL-only posts: block flattening can leave empty innerText on some DOM shapes
     (e.g. bare <a> or text nodes). Fall back to the live editor + anchor hrefs. */
  if (!text) {
    text = (editorContent.innerText || "").trim();
  }
  if (!text) {
    const links = editorContent.querySelectorAll("a[href]");
    if (links.length) {
      text = [...links]
        .map((a) => {
          const inner = (a.textContent || "").trim();
          const href = (a.getAttribute("href") || "").trim();
          return inner || href;
        })
        .filter(Boolean)
        .join("\n");
    }
  }
  if (!text) {
    text = (editorContent.textContent || "").trim();
  }

  return text || undefined;
};

export const markdownToMarkup = (markdownText) => {
  if (!markdownText) return "";
  const tempDiv = document.createElement("div");
  tempDiv.textContent = markdownText;
  let html = tempDiv.innerHTML;
  html = html.replace(/\r\n/g, "\n");
  html = html.replace(/\n/g, "<br>");
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
  html = html.replace(/__(.*?)__/g, "<strong>$1</strong>");
  html = html.replace(/(^|\W)_(.*?)_($|\W)/g, "$1<em>$2</em>$3");
  return html;
};

export const handleMarkdown = (message) => {
  const new_message = message.split("\n").join("  \n");
  const sanitizedMessage = new_message.replace(/<script.*?>.*?<\/script>/g, "");
  return sanitizedMessage;
};

export const handleCardHeight = (adjustHeight, setAdjustHeight) => {
  let height_iterations = 0;
  const max_height_iterations = 20;

  const adjust = () => {
    height_iterations++;
    setTimeout(() => {
      const messages_list = document.querySelector("#chat-box-body");
      const input_field = document.querySelector("#write-message-form");

      if (messages_list && input_field) {
        const input_height = input_field.offsetHeight;
        if (adjustHeight) {
          const height = `calc(72vh - ${60 + input_height}px + 50px)`;
          messages_list.style.height = height;
          messages_list.style.overflow = "auto";
          setAdjustHeight(false);
        }
      } else if (height_iterations < max_height_iterations) {
        adjust();
      }
    }, 100);
  };

  adjust();
};

export const handlePaste = (e) => {
  e.preventDefault();
  const text = e.clipboardData.getData("text/plain");
  document.execCommand("insertText", false, text);
};

export const isMoreHeight = (messageText, files) => {
  if (isEmptyMessage(messageText) && files.length === 0) {
    return "flex-row half-width";
  }
  const inputBox = document.getElementById("text-input-box");
  if (inputBox?.offsetHeight > 50) {
    return "flex-column full-width";
  }
  return "flex-row";
};

export const getFilePreviewUrl = (file, s3baseUrl) => {
  return is_file_uploaded(file) ? s3baseUrl + file : URL.createObjectURL(file);
};

export const fetchImageFromUrl = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
};

export const getCroppedImage = async (image, crop, originalFile) => {
  if (!crop || !image) {
    logger.error("No crop data or image reference");
    return null;
  }

  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext("2d");

  // Preserve transparency by clearing canvas with transparent pixels
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height
  );

  return new Promise((resolve) => {
    // Determine the correct file type and extension
    let fileType = "image/png"; // Default to PNG for transparency support
    let fileExt = "png";

    if (originalFile) {
      if (typeof originalFile === "string") {
        // Extract extension from URL
        const urlParts = originalFile.split(".");
        fileExt = urlParts[urlParts.length - 1].toLowerCase();
        fileType = `image/${fileExt === "jpg" ? "jpeg" : fileExt}`;
      } else {
        // Use the original file's type
        fileType = originalFile.type || "image/png";
        fileExt = fileType.split("/").pop();
        if (fileExt === "jpeg") fileExt = "jpg";
      }
    }

    const needsTransparency =
      fileType === "image/png" || fileType === "image/gif";
    const outputType = needsTransparency ? "image/png" : fileType;

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          logger.error("Canvas is empty");
          resolve(null);
          return;
        }

        let fileName = "cropped-image.png"; // Default name with PNG
        if (originalFile) {
          if (typeof originalFile === "string") {
            const urlParts = originalFile.split("/");
            const originalName = urlParts[urlParts.length - 1].replace(
              /\.[^/.]+$/,
              ""
            );
            fileName = `${originalName}-cropped.${fileExt}`;
          } else {
            const originalName =
              originalFile.name.replace(/\.[^/.]+$/, "") || "cropped-image";
            fileName = `${originalName}-cropped.${fileExt}`;
          }
        }

        const file = new File([blob], fileName, {
          type: outputType,
          lastModified: Date.now(),
        });
        resolve(file);
      },
      outputType, // Use the determined output type
      outputType === "image/jpeg" ? 0.9 : 1 // Only apply quality for JPEG
    );
  });
};

export const get_message_status = (result, message) => {
  if (!message?._id || !result?.messages_status?.length) return;

  const statuses = result.messages_status.filter(
    (status) => status.messageId === message._id
  );
  if (!statuses.length) return;

  // Staff → student/client: use the recipient's status
  const recipient =
    statuses.find((status) => status.userType === USER_TYPE.STUDENT) ||
    statuses.find((status) => status.userType === USER_TYPE.CLIENT);
  if (recipient) return recipient.messageStatus;

  // Student/client → staff: best status among staff (any seen wins)
  const staffStatuses = statuses.filter(
    (status) =>
      status.userType !== USER_TYPE.STUDENT &&
      status.userType !== USER_TYPE.CLIENT
  );
  if (!staffStatuses.length) return;
  if (staffStatuses.some((status) => status.messageStatus === "seen")) {
    return "seen";
  }
  if (staffStatuses.some((status) => status.messageStatus === "delivered")) {
    return "delivered";
  }
  return "sent";
};

export const get_online_status = (member) => {
  if (member.is_online) return "Active Now";
  return member.last_seen
    ? `Last seen ${moment(member.last_seen).fromNow()}`
    : "No last seen available";
};

export const onInputEmojiClick = (props) => {
  const { emojiObject, inputRef, handleChange } = props;
  const emoji = emojiObject.emoji;
  const editor = inputRef.current;

  if (!editor) return;

  const selection = window.getSelection();
  let range;

  const isEditorFocused =
    document.activeElement === editor &&
    selection.rangeCount > 0 &&
    editor.contains(selection.anchorNode);

  if (isEditorFocused) {
    // Use current selection range
    range = selection.getRangeAt(0);
  } else {
    // Focus and insert at the end
    editor.focus();
    range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false); // Move to end
    selection.removeAllRanges();
    selection.addRange(range);
  }

  // Insert emoji
  const textNode = document.createTextNode(emoji);
  range.deleteContents();
  range.insertNode(textNode);

  // Move cursor after the emoji
  range.setStartAfter(textNode);
  range.setEndAfter(textNode);
  selection.removeAllRanges();
  selection.addRange(range);

  handleChange?.();
};

export const CONSTRAINTS = { audio: true };
const VIDEO_CONSTRAINTS = {
  video: {
    width: 1280,
    height: 720,
    aspectRatio: 16 / 9,
  },
  audio: true,
};

const DEFAULT_STUN_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun.cloudflare.com:3478" },
];

const buildPeerConfig = () => {
  const turnUser = import.meta.env.VITE_TURN_USERNAME;
  const turnCred = import.meta.env.VITE_TURN_CREDENTIAL;
  // Prefer config.json (no .env needed). CI may still inject VITE_PEER_KEY.
  const peerKey = import.meta.env.VITE_PEER_KEY || configPeerKey;

  const iceServers = [...DEFAULT_STUN_SERVERS];
  if (turnUser && turnCred && turnIceUrls?.length) {
    iceServers.push({
      urls: turnIceUrls,
      username: turnUser,
      credential: turnCred,
    });
  }

  const base = {
    config: {
      iceServers,
    },
  };

  if (!peerHost) return base;

  return {
    ...base,
    host: peerHost,
    secure: peerSecure !== false,
    port: peerPort ?? 443,
    path: peerPath ?? "/peerjs",
    ...(peerKey ? { key: peerKey } : {}),
  };
};

export const PEER_CONFIG = buildPeerConfig();

export {
  isPeerConnectionReady,
  PEER_RECONNECT_BASE_MS,
  PEER_RECONNECT_MAX_MS,
  PEER_ENSURE_TIMEOUT_MS,
  createPeerLifecycle,
} from "./peerLifecycle.js";

export const ERROR_MESSAGES = {
  mic: "Microphone access is denied. Please grant permission in your browser settings.",
  camera:
    "Camera permission is denied. Please enable it in your browser settings.",
};

const showNotification = (data) => {
  const { message, members } = data;
  const findSender = members.find((user) => user._id === message.sender_id);

  const notificationTitle = `${findSender.name} sent a chat message`;
  const profileImage = findSender?.profile_image
    ? s3baseUrl + findSender.profile_image
    : null;

  const convertedMessage = data.message.message
    ? markdown_conversion(data.message.message)
    : "Photo";

  new Notification(notificationTitle, {
    body: convertedMessage,
    ...(profileImage ? { icon: profileImage } : {}),
  });
};

export const handleShowNotification = (dataObject) => {
  const { message, members } = dataObject;
  const findSender = members.find((user) => user._id === message.sender_id);
  if (findSender.user_type === USER_TYPE.STUDENT) {
    if (Notification.permission === "granted") {
      showNotification(dataObject);
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          showNotification(dataObject);
        }
      });
    }
  }
};

export const GetVideoStream = async (localStream) => {
  let stream = null;
  const camera = await navigator.permissions.query({ name: "camera" });
  if (camera.state === "denied") {
    return;
  }
  localStream?.getTracks().forEach((track) => track.stop());
  try {
    stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
  } catch (error) {
    if (error.name === "NotAllowedError") {
      stream = null;
    } else {
      stream = null;
    }
  }
  return stream;
};

export const getStreamStatus = (stream) => {
  if (!stream)
    return { isMuted: false, isPaused: true, isScreenSharing: false };

  const audioTracks = stream.getAudioTracks();
  const videoTracks = stream.getVideoTracks();

  return {
    isMuted: audioTracks.length === 0 || !audioTracks[0].enabled,
    isPaused: videoTracks.length === 0 || !videoTracks[0].enabled,
    isScreenSharing: videoTracks.some(
      (track) => track.kind === "video" && track.label.includes("screen")
    ),
  };
};
